import { useEffect, RefObject } from "react";

/**
 * Pull-to-refresh gesture handler for a scrollable container.
 *
 * Attaches touch listeners to the element behind `scrollRef`. While the
 * container is at scrollTop=0, vertical drag distance is mapped (with
 * resistance) onto a visible indicator. Releasing past `threshold`
 * triggers `onRefresh()`; releasing earlier snaps the indicator back.
 *
 * Why not a library:
 *   - We're on a Capacitor WebView, native PTR doesn't fire.
 *   - react-simple-pull-to-refresh wraps content in extra divs and
 *     fights our existing layout's `overflow-y-auto` main element.
 *   - This stays under ~40 LOC and only runs while the finger is down.
 *
 * The indicator element must exist in the DOM with id="ptr-indicator".
 * We mutate its style.transform / opacity directly to avoid React
 * re-renders during the gesture (60fps requirement).
 *
 * Pass `enabled: false` to detach the listeners entirely — the gesture is
 * only wanted on the few pages whose content genuinely goes stale, not on
 * every screen (a full reload in the middle of a form or a quiz is hostile).
 */
export function usePullToRefresh(
  scrollRef: RefObject<HTMLElement>,
  onRefresh: () => void,
  options: { threshold?: number; maxPull?: number; enabled?: boolean } = {}
): void {
  const threshold = options.threshold ?? 80;
  const maxPull = options.maxPull ?? 150;
  const enabled = options.enabled ?? true;

  useEffect(() => {
    if (!enabled) return;
    const el = scrollRef.current;
    if (!el) return;

    let startY = 0;
    let currentDelta = 0;
    let pulling = false;

    const getIndicator = () => document.getElementById("ptr-indicator");

    const onStart = (e: TouchEvent) => {
      if (el.scrollTop > 0) return;
      startY = e.touches[0].clientY;
      pulling = true;
      currentDelta = 0;
      const ind = getIndicator();
      if (ind) ind.style.transition = "";
    };

    const resetIndicator = () => {
      const ind = getIndicator();
      if (ind) {
        ind.style.transition = "transform 0.2s ease-out, opacity 0.2s ease-out";
        ind.style.transform = "translateY(0px)";
        ind.style.opacity = "0";
      }
      currentDelta = 0;
    };

    const onMove = (e: TouchEvent) => {
      if (!pulling) return;

      // The container has started scrolling — the user wants to scroll
      // the list, not pull. This catches the long-list false-positive
      // case where touchstart fires during momentum and scrollTop is
      // briefly 0 (rubber-band bounce) before settling >0.
      if (el.scrollTop > 0) {
        pulling = false;
        resetIndicator();
        return;
      }

      const delta = e.touches[0].clientY - startY;

      // 15px downward deadzone before activating the indicator. Filters
      // out finger jitter and the small drift that happens during a
      // tap-then-scroll on long lists. Below the deadzone we behave as
      // if no pull is in progress.
      if (delta < 15) {
        resetIndicator();
        return;
      }

      // 0.5x resistance + subtract the deadzone so the indicator starts
      // from zero offset the moment the user *actually* commits to pulling.
      currentDelta = Math.min((delta - 15) * 0.5, maxPull);
      const ind = getIndicator();
      if (ind) {
        ind.style.transition = "";
        ind.style.transform = `translateY(${currentDelta}px)`;
        ind.style.opacity = `${Math.min(currentDelta / threshold, 1)}`;
      }
    };

    const onEnd = () => {
      if (!pulling) return;
      pulling = false;
      if (currentDelta >= threshold) {
        const ind = getIndicator();
        if (ind) {
          ind.style.transition = "transform 0.2s ease-out";
          ind.style.transform = `translateY(${threshold}px)`;
        }
        onRefresh();
      } else {
        resetIndicator();
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: true });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);

    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [scrollRef, onRefresh, threshold, maxPull, enabled]);
}
